import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { validateSemiForm } from 'lib/forms/formUtils'
import { isBedbankOffer, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMaxChildAge,
  getTourV2OfferMinAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  isSalesforceTourV2,
} from 'lib/offer/offerUtils'
import { countOccupantsForProperty, generateRoomOccupants, countOccupantsForBundleAndSave } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

interface Props {
  rooms?: Array<App.RoomOccupants>;
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer;
  open?: boolean;
  capacities?: Array<App.RoomCapacity>;
  childrenAgeLabel?: string
  className?: string;
  bundleOfferId?: string;
  disableMultiRoom?: boolean;
  maxRooms?: number;
}

const defaultRooms = [generateRoomOccupants()]

function OfferCapacityWithAgesSelection(props: Props) {
  const {
    rooms = defaultRooms,
    offer,
    open,
    capacities,
    childrenAgeLabel = 'Age at time of check-in',
    className,
    bundleOfferId,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
    maxRooms,
  } = props

  const pickerRef = useRef<HTMLDivElement>(null)

  const dispatch = useOfferPageDispatch()

  useEffect(() => {
    if (pickerRef.current) {
      if (open) {
        validateSemiForm(pickerRef.current)
      }
    }
  }, [open])

  const onGuestChange = useCallback((nextRooms: Array<App.RoomOccupants>) => {
    if (isBedbankOffer(offer) || isTourV2Offer(offer)) {
      dispatch({ type: OfferPageActions.setRooms, rooms: nextRooms })
    } else {
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: nextRooms.map(({ roomId, ...rest }) => ({
          ...(isBundleOffer(offer) ? countOccupantsForBundleAndSave([rest], offer) : countOccupantsForProperty([rest], offer.property)),
          roomId,
        })),
      })
    }
  }, [offer, dispatch])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbankOffer(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      if (isSalesforceTourV2(offer)) {
        return getTourV2OfferMinAge(offer)
      }
      return getTourV2OfferMaxChildAge(offer)
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && isSalesforceTourV2(offer)), [offer])

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  return (
    <OccupancyPicker
      ref={pickerRef}
      bundleOfferId={bundleOfferId}
      occupancies={rooms}
      saleUnit={offer.saleUnit}
      onChange={onGuestChange}
      maxChildAge={childrenAgeThreshold}
      maxInfantAge={infantAgeThreshold}
      capacities={capacities}
      data-testid="offer-capacity-with-ages-selection-dropdown"
      className={className}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      childVariant={isLETour ? 'none' : undefined}
      offerType={offer.type}
      offerId={offer.id}
      maxRooms={maxRooms}
    />
  )
}

export default withOfferPageAndReduxState((state) => ({
  bundleOfferId: state.bundleOffer?.id,
  rooms: state.rooms,
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
  open: state.activeFilter === 'capacity' || state.activeSubFilter === 'capacity' || state.activeHeaderFilter === 'capacity',
}), OfferCapacityWithAgesSelection, preventRenderWithClosedFilter)
